// A07 — Inline password reset for an app user. TVP2 §9 styling: bordered
// Paper card, Bootstrap-blue Submit, MUI standard inputs. Mirrors the
// admin Reset Password card on AdminUserEdit (other-admin variant): only
// New + Confirm — no old password — because the caller is acting WITH
// can_manage_app_users, not as the target user.

import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { tokens } from "@findgeeks/ui-tokens";
import { ApiError, apiFetch } from "@/lib/api";
import { useToast } from "@/components/Toast";

interface AppUserDetail {
  profile: {
    id: string;
    display_name: string | null;
    first_name: string | null;
    last_name: string | null;
    username: string | null;
    email: string | null;
    role: string;
  };
}

function friendlyError(err: unknown, fallback: string): string {
  if (!(err instanceof ApiError)) {
    return err instanceof Error ? err.message : fallback;
  }
  const code =
    (err.body && typeof err.body === "object" && "error" in err.body
      ? String((err.body as { error?: unknown }).error ?? "")
      : "") || err.message;
  switch (code) {
    case "app_user_not_found":
      return "App user not found.";
    case "invalid_target_role":
      return "This user can't be reset from this page.";
    case "update_failed":
      return "Couldn't update the password. Try again.";
    case "bad_request":
      return "Invalid input. Double-check the form and try again.";
    default:
      return err.message || fallback;
  }
}

export function AppUserResetPassword() {
  const { id = "" } = useParams<{ id: string }>();
  const { show, ToastEl } = useToast();

  const detail = useQuery<AppUserDetail>({
    queryKey: ["app-users", id],
    queryFn: () => apiFetch<AppUserDetail>(`/v1/admin/app-users/${id}`),
    enabled: Boolean(id),
  });

  const [pwd, setPwd] = useState({ next: "", confirm: "" });
  const pwdMatch = pwd.next === pwd.confirm;
  const pwdLongEnough = pwd.next.length >= 8;
  const pwdReady = pwdLongEnough && pwdMatch;

  const setPassword = useMutation({
    mutationFn: () =>
      apiFetch(`/v1/admin/app-users/${id}/set-password`, {
        method: "POST",
        body: { new_password: pwd.next },
      }),
    onSuccess: () => {
      show("Password updated. The user can sign in with the new password.", "success");
      setPwd({ next: "", confirm: "" });
    },
    onError: (err) => show(friendlyError(err, "Reset failed"), "error"),
  });

  if (detail.isLoading) {
    return (
      <Box sx={{ p: 3, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }
  if (detail.isError || !detail.data) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">Failed to load user</Typography>
      </Box>
    );
  }

  const cardSx = {
    border: `1px solid ${tokens.colors.admin.border}`,
    borderRadius: 1,
    p: 3,
  };
  const sectionHeading = {
    fontSize: tokens.font.sizes.body,
    fontWeight: tokens.font.weights.semibold,
    color: tokens.colors.admin.textHeading,
    mb: 2,
  } as const;
  const baseBtn = {
    px: 4,
    textTransform: "none" as const,
    fontWeight: tokens.font.weights.medium,
    color: "#fff",
    boxShadow: "none",
  };
  const primaryBtn = {
    ...baseBtn,
    backgroundColor: "#007bff",
    "&:hover": { backgroundColor: "#0069d9", boxShadow: "none" },
    "&.Mui-disabled": {
      backgroundColor: "#7BB1F0",
      color: "rgba(255,255,255,0.85)",
    },
  };

  return (
    <Box sx={{ p: 3 }}>
      <Paper elevation={0} sx={cardSx}>
        <Typography sx={sectionHeading}>Reset User Password</Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr 1fr" },
            gap: 3,
            mb: 3,
          }}
        >
          <TextField
            variant="standard"
            fullWidth
            required
            label="New Password"
            type="password"
            helperText="Minimum 8 characters"
            InputLabelProps={{ shrink: true }}
            value={pwd.next}
            onChange={(e) => setPwd({ ...pwd, next: e.target.value })}
          />
          <TextField
            variant="standard"
            fullWidth
            required
            label="Confirm New Password"
            type="password"
            error={pwd.confirm.length > 0 && !pwdMatch}
            helperText={
              pwd.confirm.length > 0 && !pwdMatch ? "Passwords don't match" : " "
            }
            InputLabelProps={{ shrink: true }}
            value={pwd.confirm}
            onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })}
          />
        </Box>
        <Button
          variant="contained"
          sx={primaryBtn}
          disabled={!pwdReady || setPassword.isPending}
          onClick={() => setPassword.mutate()}
        >
          Submit
        </Button>
      </Paper>
      {ToastEl}
    </Box>
  );
}
